import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Loading from '../components/ui/Loading';
import FieldError from '../components/ui/FieldError';
import { encryptPasswordWithKeyFile } from '../utils/encryption';

import logoImage from '../assets/images/logonewnobg.png';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Error and success states
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Status checking state for login flow
  const [isLoginCheckingStatus, setIsLoginCheckingStatus] = useState(false);

  // Terms agreement state
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isCheckingStatus, applicationStatus, userId, updateUserId } = useAuth();

  // Get the intended destination from ProtectedRoute redirect
  // If no redirect state, check current path - if already on a protected page, stay there
  const currentPath = location.pathname;
  const isOnProtectedPage = ['/business-type', '/representative-details', '/shop-details', '/review-submit'].includes(currentPath);
  const from = location.state?.from?.pathname || (isOnProtectedPage ? currentPath : '/business-type');

  // Reset all states when component mounts or when user is authenticated
  useEffect(() => {
    if (isAuthenticated && !isCheckingStatus && applicationStatus !== null) {
      // If user is already authenticated and status is determined, redirect to intended destination
      // Let ProtectedRoute handle the status-based navigation
      navigate(from, { replace: true });
    } else {
      // Reset all states when component mounts
      setUsername('');
      setPassword('');
      setIsLoading(false);
      setUsernameError('');
      setPasswordError('');
      setGeneralError('');
      setSuccessMessage('');
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when user starts typing
  const clearErrors = () => {
    setUsernameError('');
    setPasswordError('');
    setGeneralError('');
    setSuccessMessage('');
  };

  // Handle terms agreement checkbox
  const handleTermsAgreement = (e) => {
    setAgreedToTerms(e.target.checked);
    clearErrors();
  };

  // Handle username input change
  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    clearErrors();
  };

  // Handle password input change
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    clearErrors();
  };

  const handleLogin = async () => {
    clearErrors();

    // Validation
    if (!username.trim()) {
      setUsernameError('Please enter your username');
      return;
    }
    if (!password.trim()) {
      setPasswordError('Please enter your password');
      return;
    }
    if (!agreedToTerms) {
      setGeneralError('Please agree to the Terms and Conditions and Privacy Policy to continue');
      return;
    }

    setIsLoading(true);

    try {
      // Encrypt password with public key
      let encryptedPassword;
      try {
        // You can customize the path to your PEM file here
        // Default is '/public.pem' in the public folder
        const publicKeyPath = process.env.REACT_APP_PUBLIC_KEY_PATH || '/public.pem';
        encryptedPassword = await encryptPasswordWithKeyFile(password, publicKeyPath);
      } catch (encryptError) {
        const errorMessage = encryptError.message || 'Failed to encrypt password';
        setGeneralError(errorMessage.includes('load') 
          ? `Failed to load encryption key. ${errorMessage}` 
          : `Encryption failed: ${errorMessage}`);
        setIsLoading(false);
        return;
      }

      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const fullUrl = `${baseUrl}/auth/signin-web`;

      const formData = new URLSearchParams({
        username: username.trim(),
        password_hash: encryptedPassword,
      });

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
        body: formData,
      });

      if (response.status === 200) {
        const responseData = await response.json();
        const userId = responseData.user_id || responseData.id || responseData.userId;
        
        if (userId) {
          updateUserId(userId);
        }

        setSuccessMessage('Login successful! Welcome to VReels Shop.');

        // Set checking status state and login (which will check status automatically)
        setIsLoginCheckingStatus(true);
        try {
          await login({
            id: userId,
            username: username.trim(),
          });

          // After login completes (which includes status check), navigate based on status
          // This will be handled by the ProtectedRoute component
          navigate(from, { replace: true });
        } catch (loginErr) {
          // Fallback to default flow
          navigate(from, { replace: true });
        } finally {
          setIsLoginCheckingStatus(false);
        }
      } else {
        // Try to get error details from response
        let errorMessage = 'Invalid username or password. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If response is not JSON, try to get text
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (textError) {
            // Failed to parse error response
          }
        }
        
        // Set appropriate error field
        const lowerMessage = errorMessage.toLowerCase();
        if (lowerMessage.includes('username') || lowerMessage.includes('user')) {
          setUsernameError(errorMessage);
        } else if (lowerMessage.includes('password') || lowerMessage.includes('credential')) {
          setPasswordError(errorMessage);
        } else {
          setGeneralError(errorMessage);
        }
      }
    } catch (error) {
      setGeneralError('Unable to connect. Please check your connection and try again.');
    }

    setIsLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleLogin();
  };


  return (
    <div className="login-container">
      <div className="login-content">
        {/* Left Side - Login Form */}
        <div className="login-form-section fade-in">
          <div className="login-form-container">
            <div className="login-header">
              <h1 className="login-title">Sign In</h1>
              <p className="login-subtitle">
                Enter your username and password to continue
              </p>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-group">
                <label htmlFor="username" className="input-label">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={username}
                  onChange={handleUsernameChange}
                  placeholder="Enter your username"
                  className={`input-field ${usernameError ? 'error' : ''}`}
                  autoComplete="username"
                  required
                />
                <FieldError error={usernameError} />
              </div>

              <div className="input-group">
                <label htmlFor="password" className="input-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                  className={`input-field ${passwordError ? 'error' : ''}`}
                  autoComplete="current-password"
                  required
                />
                <FieldError error={passwordError} />
                <div className="forgot-password-link">
                  <a 
                    href="/forgot-password" 
                    className="forgot-password-text"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/forgot-password');
                    }}
                  >
                    Forgot Password?
                  </a>
                </div>
              </div>

              <FieldError error={generalError} />

              <button 
                type="submit" 
                className={`btn btn-primary login-btn ${isLoading ? 'loading' : ''} ${!username.trim() || !password.trim() || !agreedToTerms ? 'disabled' : ''}`}
                disabled={isLoading || !username.trim() || !password.trim() || !agreedToTerms}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            {/* Status Checking Loading State */}
            {isLoginCheckingStatus && (
              <div className="status-check-container">
                <div className="status-check-content">
                  <Loading />
                  {/* <h3 className="status-check-title">Checking Application Status...</h3>
                  <p className="status-check-subtitle">Please wait while we verify your application status.</p> */}
                </div>
              </div>
            )}

            <div className="login-footer">
              <div className="terms-agreement">
                <label className="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={handleTermsAgreement}
                    className="terms-checkbox"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="terms-text">
                    I agree to the{' '}
                    <a href="/terms-and-conditions" className="terms-link" target="_blank" rel="noopener noreferrer">Terms and conditions</a> and{' '}
                    <a href="/privacy-policy" className="terms-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Welcome Content */}
        <div className="welcome-section slide-in-right">
          <div className="welcome-content">
            <div className="brand-section">
              <div className="brand-logo">
                <img src={logoImage} alt="VReels Shop" className="welcome-logo" />
              </div>
              <h1 className="brand-title">Sell, Shine & Succeed</h1>
              <p className="brand-subtitle">Your Shop, Your Stage - VReels Marketplace Awaits!</p>
              <p className="brand-tagline">Join a growing community of creators & vendors, and turn your products into a movement. Showcase your brand, link products with Reels & Pix, and grow with us.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
